import type { Lesson } from '../../../types';

export const MATH_FRACTIONS_GAP_LESSONS: Record<string, Lesson> = {

  /* ── LEKSJON 3 — Legge sammen brøker med samme nevner ───────── */

  'fractions-adding-same': {
    id: 'fractions-adding-same',
    pathwayId: 'math-fractions-8-12',
    subject: 'mathematics',
    strand: 'fractions',
    ageBand: '8-12',
    phase: 'builder',
    level: 2,
    language: 'no',
    title: 'Legge sammen brøker: hvorfor blir ikke bitene mindre?',
    titleEn: "Adding fractions: why don't the pieces get smaller?",
    durationMinutes: 35,
    saganHook: {
      type: 'question',
      content: 'Vaffelplata har fem hjerter. Du spiser to, pappa spiser tre. Nå er plata helt tom — det ligger ikke igjen så mye som en smule på fatet. Men veldig mange regner 2/5 + 3/5 og får 5/10. Og 5/10 er en halv plate. Se på fatet igjen: hvor skulle den halve vaffelen ha ligget? Noe i det regnestykket må være galt, og i dag finner du ut nøyaktig hva.',
    },
    saganHookEn: "A Norwegian waffle is baked as one round plate of five hearts. You eat two hearts, your dad eats three. Now the plate is completely empty — there is not a crumb left on it. But a lot of people work out 2/5 + 3/5 and get 5/10. And 5/10 is half a plate. Look at the plate again: where would that half waffle be sitting? Something in that calculation must be wrong, and today you find out exactly what.",
    realWorldBridge: {
      context: 'Vaffelplata, målebegeret og 4/4-takten',
      content: 'Du legger sammen brøker nesten hver uke uten å tenke over det. Vaffeljernet lager fem hjerter, og dere teller hvor mange som er igjen på fatet. I oppskriften står det 3/4 dl melk i røra og 3/4 dl til slutt — og noen må vite hvor mye melk det blir til sammen. I musikken går en vanlig takt i 4/4: fire fjerdedeler fyller nøyaktig én takt, og spiller du en for mye, hører hele bandet at det ble feil. Det alle disse har felles: bitene er like store. Og når bitene er like store, er det bare å telle dem.',
    },
    realWorldBridgeEn: {
      context: 'Waffles, measuring jugs and 4/4 time',
      content: "You add fractions almost every week without noticing. A Norwegian waffle iron makes five hearts, and you count how many are left on the plate. A recipe asks for 3/4 dl of milk in the batter and 3/4 dl at the end — someone has to work out how much milk that is altogether. In music, an ordinary bar is 4/4 time: four quarter notes fill exactly one bar, and if you play one too many, the whole band hears it. What all of these have in common: the pieces are the same size. And when the pieces are the same size, all you have to do is count them.",
    },
    coreConcept: {
      definition: 'Når to brøker har samme nevner, legger du sammen tellerne og lar nevneren stå. Nevneren er ikke et antall — den er navnet på hvor store bitene er.',
      terminology: [
        { term: 'teller', definition: 'tallet over brøkstreken — hvor mange biter du har' },
        { term: 'nevner', definition: 'tallet under brøkstreken — hvor mange like biter helheten er delt i, altså hvor store bitene er' },
        { term: 'lik nevner', definition: 'brøker med biter av samme størrelse, som kan telles sammen direkte' },
        { term: 'én hel', definition: 'når telleren er lik nevneren: 5/5, 8/8 og 12/12 er alle nøyaktig 1' },
        { term: 'uekte brøk', definition: 'en brøk der telleren er større enn nevneren, som 6/4 — den er mer enn én hel' },
      ],
      explanation: 'Tenk på nevneren som et etternavn. 2/5 leses «to femdeler», og femdel er navnet på bitestørrelsen. Legger du to femdeler sammen med tre femdeler, får du fem femdeler — akkurat som at 2 epler pluss 3 epler blir 5 epler, ikke 5 pærer. Bitene blir aldri mindre av at du samler flere av dem. Derfor er 2/5 + 3/5 = 5/5, og 5/5 er nøyaktig én hel plate. Det stemmer med det tomme fatet.\n\nFeilen 5/10 kommer av å legge sammen nevnerne også. Prøv å oversette svaret til ord: det påstår at når du samler femdeler, blir de plutselig til tideler. Tideler er mindre biter — halvparten så store. Men ingen har klippet noe. Regelen er derfor kort nok til å huske: telleren teller, nevneren navngir.\n\nSubtraksjon fungerer helt likt. 7/8 − 3/8 er «sju åttedeler minus tre åttedeler», altså fire åttedeler = 4/8. Og fordi fire av åtte biter er akkurat halvparten, er 4/8 det samme som 1/2 — der møter du igjen de ekvivalente brøkene fra forrige leksjon.\n\nNoen ganger passerer summen én hel. 3/4 + 3/4 = 6/4. Seks fjerdedeler er mer enn de fire fjerdedelene som utgjør én hel, så du har én hel og 2/4 til overs: 1 1/2. En brøk der telleren er større enn nevneren kalles en uekte brøk. Den er ikke feil — den forteller bare at du har mer enn én helhet.',
      workedExamples: [
        {
          problem: '2/5 + 3/5 = ?',
          solution: '5/5 = 1 hel plate',
          explanation: 'Femdeler pluss femdeler blir femdeler: 2 + 3 = 5 biter. Nevneren står stille på 5. Når telleren er lik nevneren, har du nøyaktig én hel — og fatet er tomt, akkurat som det skal være.',
        },
        {
          problem: 'En sjokolade er delt i 8 biter. 7/8 − 3/8 = ?',
          solution: '4/8, som er det samme som 1/2',
          explanation: 'Du trekker fra biter av samme størrelse: 7 − 3 = 4. Du har 4/8 igjen, og fire av åtte biter er akkurat halvparten av sjokoladen.',
        },
        {
          problem: '3/4 + 3/4 = ?',
          solution: '6/4 = 1 hel og 2/4, altså 1 1/2',
          explanation: 'Seks fjerdedeler. Fire av dem fyller én hel, og da er det 2/4 igjen. Summen passerte én hel — det er helt lov, og en slik brøk kalles en uekte brøk.',
        },
        {
          problem: 'Hvorfor er 2/5 + 3/5 ikke 5/10?',
          solution: 'Fordi nevneren ikke er et antall som skal legges sammen',
          explanation: 'Les svaret høyt: 5/10 betyr fem tideler. Men ingen delte plata i ti biter — den hadde fem hjerter hele veien. Nevneren navngir bitene, og navnet endrer seg ikke av at du samler flere av dem.',
        },
      ],
    },
    coreConceptEn: {
      definition: 'When two fractions share the same denominator, you add the numerators and leave the denominator alone. The denominator is not a count — it is the name for how big the pieces are.',
      terminology: [
        { term: 'numerator', definition: 'the number above the bar — how many pieces you have' },
        { term: 'denominator', definition: 'the number below the bar — how many equal pieces the whole is cut into, so how big each piece is' },
        { term: 'like denominators', definition: 'fractions whose pieces are the same size, so they can be counted together directly' },
        { term: 'one whole', definition: 'when the numerator equals the denominator: 5/5, 8/8 and 12/12 are all exactly 1' },
        { term: 'improper fraction', definition: 'a fraction whose numerator is bigger than its denominator, like 6/4 — it is more than one whole' },
      ],
      explanation: 'Think of the denominator as a surname. 2/5 is read "two fifths", and "fifth" is the name of the piece size. Add two fifths to three fifths and you get five fifths — exactly like 2 apples plus 3 apples making 5 apples, not 5 pears. Pieces never shrink just because you gather more of them. So 2/5 + 3/5 = 5/5, and 5/5 is exactly one whole plate. That matches the empty plate.\n\nThe answer 5/10 comes from adding the denominators too. Try saying it out loud: it claims that when you gather fifths, they suddenly turn into tenths. Tenths are smaller pieces — half the size. But nobody cut anything. So the rule is short enough to remember: the numerator counts, the denominator names.\n\nSubtraction works the same way. 7/8 − 3/8 is "seven eighths minus three eighths", which is four eighths = 4/8. And because four of eight pieces is exactly half, 4/8 is the same as 1/2 — there are the equivalent fractions from the last lesson again.\n\nSometimes the sum goes past one whole. 3/4 + 3/4 = 6/4. Six quarters is more than the four quarters that make one whole, so you have one whole and 2/4 left over: 1 1/2. A fraction whose numerator is bigger than its denominator is called an improper fraction. It is not a mistake — it just tells you that you have more than one whole.',
      workedExamples: [
        {
          problem: '2/5 + 3/5 = ?',
          solution: '5/5 = 1 whole plate',
          explanation: 'Fifths plus fifths make fifths: 2 + 3 = 5 pieces. The denominator stays at 5. When the numerator equals the denominator you have exactly one whole — and the plate is empty, just as it should be.',
        },
        {
          problem: 'A chocolate bar is split into 8 pieces. 7/8 − 3/8 = ?',
          solution: '4/8, which is the same as 1/2',
          explanation: 'You are taking away pieces of the same size: 7 − 3 = 4. You have 4/8 left, and four of eight pieces is exactly half the chocolate bar.',
        },
        {
          problem: '3/4 + 3/4 = ?',
          solution: '6/4 = 1 whole and 2/4, that is 1 1/2',
          explanation: 'Six quarters. Four of them fill one whole, which leaves 2/4 over. The sum went past one whole — that is allowed, and a fraction like this is called an improper fraction.',
        },
        {
          problem: 'Why is 2/5 + 3/5 not 5/10?',
          solution: 'Because the denominator is not a count to be added',
          explanation: 'Read the answer out loud: 5/10 means five tenths. But nobody cut the plate into ten pieces — it had five hearts the whole time. The denominator names the pieces, and that name does not change just because you collect more of them.',
        },
      ],
    },
    interactiveComponent: {
      type: 'custom',
      description: 'Prøv det med et ark og en saks. Tegn to like brede rektangler og del begge i åtte like biter. Fargelegg 5/8 i det ene og 3/8 i det andre, klipp ut de fargede bitene og legg dem etter hverandre i én rad. Hvor mange åttedeler ble det — og hva heter det tallet? Fargelegg så to biter til i det andre rektangelet, så begge har 5/8, og legg raden på nytt. Nå blir den lengre enn én hel: hva blir telleren, og hvor mye stikker utenfor?',
      phetUrl: null,
    },
    practiceQuestions: [
      {
        id: 'fas-q1',
        type: 'multiple_choice',
        question: 'Vaffelplata har 5 hjerter. Du spiser 2/5 og pappa spiser 3/5. Hvor mye av plata er spist til sammen?',
        options: ['5/5 — hele plata', '5/10 — en halv plate', '6/5 — mer enn hele plata', '2/5 — bare din del'],
        correctAnswer: '5/5 — hele plata',
        explanation: 'Femdeler pluss femdeler blir femdeler: 2 + 3 = 5. Når telleren er lik nevneren, har du én hel. Det stemmer med det tomme fatet — hadde svaret vært 5/10, måtte det ligget igjen to og et halvt hjerte.',
        solutionSteps: [
          '🧇 Begge brøkene har nevner 5, så bitene er like store',
          'Tell bitene: 2 hjerter + 3 hjerter = 5 hjerter, og nevneren står stille',
          '5/5 er nøyaktig én hel plate ✓',
        ],
      },
      {
        id: 'fas-q2',
        type: 'multiple_choice',
        question: '4/9 + 2/9 = ?',
        options: ['6/9', '6/18', '8/9', '24/9'],
        correctAnswer: '6/9',
        explanation: 'Legg sammen tellerne: 4 + 2 = 6. Nevneren 9 er navnet på bitestørrelsen og skal ikke legges sammen. Svaret er 6/9 (som kan forenkles til 2/3 hvis du vil).',
        solutionSteps: [
          '🔢 Samme nevner (9) betyr biter av samme størrelse',
          'Legg sammen tellerne: 4 + 2 = 6',
          'Nevneren blir stående, så svaret er 6/9 ✓',
        ],
      },
      {
        id: 'fas-q3',
        type: 'multiple_choice',
        question: 'Eggekartongen har 12 fordypninger. Det ligger erter i 11 av dem, og du tar ut 5. Hvor mye er igjen?',
        options: ['6/12', '6/24', '16/12', '5/12'],
        correctAnswer: '6/12',
        explanation: 'Du trekker fra biter av samme størrelse: 11 − 5 = 6. Nevneren står stille fordi kartongen fortsatt har 12 fordypninger. Svaret er 6/12 — akkurat halve kartongen, altså det samme som 1/2.',
        solutionSteps: [
          '🥚 Alle bitene er tolvdeler, for kartongen har 12 fordypninger',
          'Trekk fra tellerne: 11 − 5 = 6',
          'Nevneren står stille: 6/12, altså en halv kartong ✓',
        ],
      },
      {
        id: 'fas-q4',
        type: 'multiple_choice',
        question: 'Du går 3/4 km på ski før pausen og 3/4 km etter pausen. Hvor langt har du gått til sammen?',
        options: ['6/4 km, altså 1 1/2 km', '6/8 km', '3/4 km', '9/16 km'],
        correctAnswer: '6/4 km, altså 1 1/2 km',
        explanation: 'Tre fjerdedeler pluss tre fjerdedeler er seks fjerdedeler. Fire fjerdedeler fyller én hel kilometer, og da er det 2/4 km igjen: til sammen 1 1/2 km. Summen passerte én hel, og det er helt i orden.',
        solutionSteps: [
          '⛷ Begge strekningene måles i fjerdedeler av en kilometer',
          'Legg sammen tellerne: 3 + 3 = 6, altså 6/4 km',
          '4/4 er én hel km, og 2/4 blir igjen: 1 1/2 km ✓',
        ],
      },
    ],
    practiceQuestionsEn: [
      {
        id: 'fas-q1e',
        type: 'multiple_choice',
        question: 'A waffle plate has 5 hearts. You eat 2/5 and your dad eats 3/5. How much of the plate has been eaten altogether?',
        options: ['5/5 — the whole plate', '5/10 — half a plate', '6/5 — more than the whole plate', '2/5 — only your share'],
        correctAnswer: '5/5 — the whole plate',
        explanation: 'Fifths plus fifths make fifths: 2 + 3 = 5. When the numerator equals the denominator you have one whole. That matches the empty plate — if the answer really were 5/10, two and a half hearts would still be lying there.',
        solutionSteps: [
          '🧇 Both fractions have denominator 5, so the pieces are the same size',
          'Count the pieces: 2 hearts + 3 hearts = 5 hearts, and the denominator stays put',
          '5/5 is exactly one whole plate ✓',
        ],
      },
      {
        id: 'fas-q2e',
        type: 'multiple_choice',
        question: '4/9 + 2/9 = ?',
        options: ['6/9', '6/18', '8/9', '24/9'],
        correctAnswer: '6/9',
        explanation: 'Add the numerators: 4 + 2 = 6. The denominator 9 is the name of the piece size and must not be added. The answer is 6/9 (which simplifies to 2/3 if you like).',
        solutionSteps: [
          '🔢 The same denominator (9) means pieces of the same size',
          'Add the numerators: 4 + 2 = 6',
          'The denominator stays, so the answer is 6/9 ✓',
        ],
      },
      {
        id: 'fas-q3e',
        type: 'multiple_choice',
        question: 'An egg carton has 12 cups. There are peas in 11 of them and you take 5 out. How much is left?',
        options: ['6/12', '6/24', '16/12', '5/12'],
        correctAnswer: '6/12',
        explanation: 'You are taking away pieces of the same size: 11 − 5 = 6. The denominator stays put because the carton still has 12 cups. The answer is 6/12 — exactly half the carton, the same as 1/2.',
        solutionSteps: [
          '🥚 Every piece is a twelfth, because the carton has 12 cups',
          'Subtract the numerators: 11 − 5 = 6',
          'The denominator stays put: 6/12, which is half a carton ✓',
        ],
      },
      {
        id: 'fas-q4e',
        type: 'multiple_choice',
        question: 'You ski 3/4 km before the break and 3/4 km after it. How far have you skied altogether?',
        options: ['6/4 km, that is 1 1/2 km', '6/8 km', '3/4 km', '9/16 km'],
        correctAnswer: '6/4 km, that is 1 1/2 km',
        explanation: 'Three quarters plus three quarters is six quarters. Four quarters fill one whole kilometre, leaving 2/4 km over: 1 1/2 km in total. The sum went past one whole, and that is perfectly fine.',
        solutionSteps: [
          '⛷ Both stretches are measured in quarters of a kilometre',
          'Add the numerators: 3 + 3 = 6, so 6/4 km',
          '4/4 is one whole km and 2/4 is left: 1 1/2 km ✓',
        ],
      },
    ],
    handsOnActivity: {
      title: 'Eggekartong-brøk',
      materials: ['en tom eggekartong til 12', 'tørkede erter, små klosser, perler eller knapper', 'tusj', 'saks', 'papir og blyant'],
      instructions: [
        'Kartongen er hele. Hver fordypning er 1/12. Skriv 1/12 i én av fordypningene med tusj, så navnet på bitestørrelsen er synlig hele veien.',
        'Barnet legger 3 erter i kartongen og sier høyt: «tre tolvdeler».',
        'En voksen eller et søsken legger i 5 erter til. Les av summen sammen: 8/12. Skriv regnestykket ned på papiret: 3/12 + 5/12 = 8/12.',
        'Fortsett å legge i erter til kartongen er helt full. Hva heter brøken da? (12/12 — altså 1 hel.)',
        'Ta ut 5 erter igjen og skriv subtraksjonen: 12/12 − 5/12 = 7/12.',
        'Klipp til slutt kartongen i to like deler og legg 6 erter i den ene halvdelen. Se at 6/12 dekker nøyaktig like mye som 1/2.',
      ],
      timeMinutes: 12,
      whatToLookFor: 'Hør på ordene: sier barnet «tolvdeler» hele veien, eller begynner nevneren å skifte navn underveis? Misoppfatningen sitter akkurat der. Hvis barnet sier at 3/12 + 5/12 blir 8/24, be det telle fordypningene på nytt — kartongen har fortsatt bare 12.',
    },
    handsOnActivityEn: {
      title: 'Egg carton fractions',
      materials: ['one empty egg carton for 12', 'dried peas, small bricks, beads or buttons', 'a marker pen', 'scissors', 'paper and pencil'],
      instructions: [
        'The carton is the whole. Each cup is 1/12. Write 1/12 inside one of the cups with the marker so the name of the piece size stays visible.',
        'The child puts 3 peas in the carton and says out loud: "three twelfths".',
        'An adult or a sibling adds 5 more peas. Read the total together: 8/12. Write the calculation down: 3/12 + 5/12 = 8/12.',
        'Keep adding peas until the carton is completely full. What is the fraction now? (12/12 — that is 1 whole.)',
        'Take 5 peas out again and write the subtraction: 12/12 − 5/12 = 7/12.',
        'Finally cut the carton into two equal halves and put 6 peas in one half. See that 6/12 covers exactly as much as 1/2.',
      ],
      timeMinutes: 12,
      whatToLookFor: 'Listen to the words: does the child say "twelfths" all the way through, or does the denominator start changing its name along the way? That is exactly where the misconception lives. If the child says 3/12 + 5/12 makes 8/24, ask them to count the cups again — the carton still has only 12.',
    },
    printableWorksheet: {
      title: 'Brøker som skal legges sammen',
      sections: [
        {
          type: 'warmup',
          title: 'Oppvarming',
          instructions: 'Skriv brøken som er fylt, og les den høyt.',
          content: '1. En eggekartong til 12 med 4 erter i: ___/___\n2. En vaffelplate med 5 hjerter, 3 spist: ___/___\n3. En sjokolade med 8 biter, alle 8 spist: ___/___ (hva heter dette tallet?)',
        },
        {
          type: 'calculate',
          title: 'Regn ut',
          instructions: 'Husk regelen: telleren teller, nevneren navngir.',
          content: '1. 1/6 + 4/6 = ____\n2. 3/10 + 3/10 = ____\n3. 5/8 − 2/8 = ____\n4. 2/7 + 5/7 = ____ (hva heter svaret med ord?)\n5. 9/12 − 4/12 = ____\n6. 5/6 + 4/6 = ____ (dette svaret passerer én hel!)',
        },
        {
          type: 'critique',
          title: 'Finn feilen',
          instructions: 'Et annet barn har regnet slik. Forklar med egne ord hva som er galt.',
          content: '«2/5 + 3/5 = 5/10, fordi 2 + 3 = 5 og 5 + 5 = 10.»\n\nHva er galt? ________________________________________________\n\nTegn vaffelplata med fem hjerter og vis hvorfor svaret ikke kan stemme.',
        },
        {
          type: 'challenge',
          title: 'Utfordring',
          instructions: 'Løs oppgaven og forklar tenkemåten din.',
          content: 'Ingrid baker boller. Oppskriften skal ha 3/4 dl melk i deigen og 3/4 dl melk til pensling. Målebegeret hennes er merket i fjerdedeler.\n\na) Hvor mange fjerdedeler melk trenger hun til sammen? ____\nb) Hvor mange hele desiliter blir det, og hvor mye blir til overs? ____',
        },
        {
          type: 'reflection',
          title: 'Refleksjon',
          instructions: 'Skriv med egne ord:',
          content: 'Hvorfor endrer ikke nevneren seg når du legger sammen brøker med like store biter?\n\n_____________________________________________________________\n_____________________________________________________________',
        },
      ],
    },
    printableWorksheetEn: {
      title: 'Fractions to add together',
      sections: [
        {
          type: 'warmup',
          title: 'Warm-up',
          instructions: 'Write the fraction that is filled, and read it out loud.',
          content: '1. An egg carton for 12 with 4 peas in it: ___/___\n2. A waffle plate with 5 hearts, 3 eaten: ___/___\n3. A chocolate bar with 8 pieces, all 8 eaten: ___/___ (what is this number called?)',
        },
        {
          type: 'calculate',
          title: 'Work it out',
          instructions: 'Remember the rule: the numerator counts, the denominator names.',
          content: '1. 1/6 + 4/6 = ____\n2. 3/10 + 3/10 = ____\n3. 5/8 − 2/8 = ____\n4. 2/7 + 5/7 = ____ (what is this answer called in words?)\n5. 9/12 − 4/12 = ____\n6. 5/6 + 4/6 = ____ (this answer goes past one whole!)',
        },
        {
          type: 'critique',
          title: 'Find the mistake',
          instructions: 'Another child worked it out like this. Explain in your own words what is wrong.',
          content: '"2/5 + 3/5 = 5/10, because 2 + 3 = 5 and 5 + 5 = 10."\n\nWhat is wrong? ______________________________________________\n\nDraw the waffle plate with its five hearts and show why the answer cannot be right.',
        },
        {
          type: 'challenge',
          title: 'Challenge',
          instructions: 'Solve the problem and explain your thinking.',
          content: 'Ingrid is baking buns. The recipe needs 3/4 dl of milk in the dough and 3/4 dl of milk for brushing on top. Her measuring jug is marked in quarters.\n\na) How many quarters of milk does she need altogether? ____\nb) How many whole decilitres is that, and how much is left over? ____',
        },
        {
          type: 'reflection',
          title: 'Reflection',
          instructions: 'Write in your own words:',
          content: 'Why does the denominator not change when you add fractions whose pieces are the same size?\n\n_____________________________________________________________\n_____________________________________________________________',
        },
      ],
    },
    parentGuide: {
      whatChildShouldKnow: 'Barnet ditt skal nå kunne legge sammen og trekke fra brøker som har samme nevner, og forklare hvorfor nevneren blir stående. Det skal også kjenne igjen når en sum lander nøyaktig på én hel (5/5, 8/8, 12/12), og når den passerer én hel (6/4).',
      commonMisconceptions: 'Den vanligste feilen er å legge sammen nevnerne også, slik at 2/5 + 3/5 blir 5/10. Nevneren er ikke et antall — den er navnet på hvor store bitene er. Når du legger sammen femdeler, får du flere femdeler, aldri tideler. Det er den samme regelen som at 2 epler pluss 3 epler blir 5 epler, ikke 5 pærer. Vaffelplata avslører feilen med en gang: hvis svaret var en halv plate, måtte det ligget igjen to og et halvt hjerte på fatet.',
      dinnerTableQuestion: 'Spør barnet: «Vi har spist 3/8 av pizzaen, og så spiser vi 2/8 til. Hvor mye er spist — og hvor mye er igjen?»',
      nextLessonPreview: 'Neste gang: Hvilken brøk er størst — 2/3 eller 3/4? Begge mangler bare én bit på å bli fulle, så mange gjetter at de er like store. Vi måler etter med skje og glass.',
    },
    parentGuideEn: {
      whatChildShouldKnow: 'Your child should now be able to add and subtract fractions that share a denominator, and explain why the denominator stays put. They should also recognise when a sum lands exactly on one whole (5/5, 8/8, 12/12) and when it goes past one whole (6/4).',
      commonMisconceptions: 'The most common error is adding the denominators too, so that 2/5 + 3/5 becomes 5/10. The denominator is not a count — it is the name for how big the pieces are. Add fifths together and you get more fifths, never tenths. It is the same rule as 2 apples plus 3 apples making 5 apples, not 5 pears. The waffle plate exposes the error instantly: if the answer really were half a plate, two and a half hearts would still be sitting there.',
      dinnerTableQuestion: 'Ask your child: "We have eaten 3/8 of the pizza, and now we eat 2/8 more. How much has been eaten — and how much is left?"',
      nextLessonPreview: 'Next time: Which fraction is bigger — 2/3 or 3/4? Both are only one piece short of full, so most people guess they are equal. We will measure them out with a tablespoon and two glasses.',
    },
    openDoor: 'Nå kan du legge sammen brøker når bitene er like store. Men hva om de ikke er det? En halv vaffelplate pluss en tredjedel av den samme plata — hvor mye er det? Bitene passer ikke sammen, for de har ulike navn. Kan du finne en måte å klippe begge om til biter av samme størrelse? Du har faktisk allerede laget verktøyet selv, den gangen du laget ekvivalente brøker.',
    openDoorEn: 'Now you can add fractions when the pieces are the same size. But what if they are not? Half a waffle plate plus a third of the same plate — how much is that? The pieces do not fit together, because they have different names. Can you find a way to recut both of them into pieces of the same size? You have already built the tool yourself, back when you made equivalent fractions.',
    curriculumSources: [
      'LK20 Matematikk (MAT01-05), kompetansemål etter 5. trinn: «utvikle og bruke ulike strategiar for rekning med positive tal og brøk og forklare tenkjemåtane sine» (KM279). Målet avgrenser ikke hvilke nevnere man regner med; leksjonen holder seg bevisst til brøker med samme nevner og ligger dermed godt innenfor målet.',
      'Merk: uttrykket «uekte brøk» er ikke nevnt i noe kompetansemål i LK20. Det er tatt med her fordi summer som passerer én hel (3/4 + 3/4 = 6/4) dukker opp av seg selv med en gang man legger sammen brøker — barnet skal kjenne igjen fenomenet, ikke prøves i begrepet. Formålstjenlige strategier for brøkregning er et eksplisitt kompetansemål først etter 7. trinn.',
      'LK20 Matematikk (MAT01-05), kjerneelement «Resonnering og argumentasjon» — barnet skal begrunne hvorfor nevneren blir stående, ikke bare bruke regelen.',
    ],
    learningObjectives: [
      'Legge sammen og trekke fra brøker som har samme nevner',
      'Forklare hvorfor nevneren blir stående mens telleren endrer seg',
      'Kjenne igjen når en sum lander nøyaktig på én hel, og når den passerer én hel',
    ],
    learningObjectivesEn: [
      'Add and subtract fractions that share the same denominator',
      'Explain why the denominator stays put while the numerator changes',
      'Recognise when a sum lands exactly on one whole, and when it goes past it',
    ],
    kompetansemaal: {
      kode: 'KM279',
      text: 'utvikle og bruke ulike strategiar for rekning med positive tal og brøk og forklare tenkjemåtane sine',
    },
    generatedAt: new Date(),
    version: 1,
    source: 'seed',
  },

  /* ── LEKSJON 4 — Sammenligne brøker med ulike nevnere ────────── */

  'fractions-comparing': {
    id: 'fractions-comparing',
    pathwayId: 'math-fractions-8-12',
    subject: 'mathematics',
    strand: 'fractions',
    ageBand: '8-12',
    phase: 'builder',
    level: 3,
    language: 'no',
    title: 'Hvilken brøk er størst? Gjett først, mål etterpå',
    titleEn: 'Which fraction is bigger? Guess first, measure after',
    durationMinutes: 35,
    saganHook: {
      type: 'question',
      content: 'To helt like glass står på bordet. I det ene er det saft opp til 2/3. I det andre opp til 3/4. Begge mangler bare én bit på å være helt fulle — så da er de vel like fulle? Skriv ned hva du tror, før du leser videre. Om litt måler vi opp begge glassene med en spiseskje, og da får du se om magefølelsen din traff.',
    },
    saganHookEn: "Two identical glasses are standing on the table. One is filled with juice up to 2/3. The other up to 3/4. Both are only one piece short of being completely full — so surely they are equally full? Write down what you think before you read on. In a moment we will measure out both glasses with a tablespoon, and then you will see whether your hunch was right.",
    realWorldBridge: {
      context: 'Saftglass, oppskrifter og oppmøte på fotballtrening',
      content: 'Hver gang noen skal dele rettferdig, må noen kunne sammenligne brøker. Er det mest igjen i pakken med 3/4 eller i pakken med 5/6? Er 2/5 av klassen flere enn 1/3 av klassen? Treneren sier at 3/8 av spillerne var borte fra trening i går og 1/4 i dag — når var det flest på banen? Tallene ser nesten like store ut på papiret, men bitene har ulike navn. Og da må du gjøre noe med dem før du i det hele tatt har lov til å sammenligne.',
    },
    realWorldBridgeEn: {
      context: 'Glasses of juice, recipes and attendance at football practice',
      content: 'Every time something has to be shared fairly, somebody has to compare fractions. Is there more left in the packet with 3/4 or the one with 5/6? Are 2/5 of the class more people than 1/3 of the class? The coach says 3/8 of the players missed practice yesterday and 1/4 missed it today — when were most players on the pitch? On paper the numbers look almost the same size, but the pieces have different names. And that means you have to do something to them before you are even allowed to compare.',
    },
    coreConcept: {
      definition: 'To brøker kan bare sammenlignes når de måles mot den samme helheten. Da finnes det tre raske strategier: lik nevner, lik teller, eller sammenligning mot holdepunktene 0, 1/2 og 1.',
      terminology: [
        { term: 'sammenligne', definition: 'finne ut hvilken av to størrelser som er størst, minst, eller om de er like' },
        { term: 'fellesnevner', definition: 'en felles bitestørrelse som begge brøkene kan skrives om til' },
        { term: 'holdepunkt', definition: 'et tall du måler mot for å få et raskt overslag, som 0, 1/2 og 1' },
        { term: 'helheten', definition: 'det brøken er en del av — en sammenligning er bare rettferdig når begge brøkene kommer fra samme helhet' },
      ],
      explanation: 'Strategi 1 — lik nevner. Har brøkene samme nevner, er bitene like store, og da er det bare å telle: 5/8 er større enn 3/8, akkurat som 5 kroner er mer enn 3 kroner.\n\nStrategi 2 — lik teller. Har brøkene samme teller, har du like mange biter, men bitene er ulike. Se på 2/5 og 2/9: du har to biter i begge, men et glass delt i 5 gir større biter enn et glass delt i 9. Jo større nevneren er, jo mindre er hver bit. Derfor er 2/5 størst.\n\nStrategi 3 — holdepunkter. Ofte holder det å se hvor brøken ligger i forhold til 1/2 og 1. 5/12 er litt mindre enn en halv, for halvparten ville vært 6/12. Og 5/6 er nesten hel. Da trenger du ikke regne i det hele tatt.\n\nNår ingen av strategiene passer, lager du en felles bitestørrelse. 2/3 og 3/4 har verken lik teller eller lik nevner. Men 12 kan deles både i tredeler og fjerdedeler: 2/3 = 8/12 og 3/4 = 9/12. Nå er bitene like store, og 9 er mer enn 8. Altså er 3/4 størst. Begge manglet «én bit» — men bitene som manglet var ikke like store. En tredjedel er større enn en fjerdedel, så 2/3 mangler mest.\n\nDen farligste vanen er å behandle teller og nevner som to vanlige tall. Sammenlign 3/5 og 2/3: 3 er større enn 2, og 5 er større enn 3, så «den må jo være størst». Gjør dem om til femtendeler: 3/5 = 9/15 og 2/3 = 10/15. Det er 2/3 som er størst. En brøk er ett tall, ikke to.\n\nTil slutt, det viktigste av alt: en sammenligning gjelder bare innenfor den samme helheten. En tredjedel av en liten muffins er ikke mer kake enn en fjerdedel av en stor bløtkake, selv om 1/3 er større enn 1/4. Spør alltid: en tredjedel av hva?',
      workedExamples: [
        {
          problem: 'Hvilken er størst: 3/8 eller 5/8?',
          solution: '5/8',
          explanation: 'Samme nevner betyr like store biter. Da er det bare å telle bitene: 5 er mer enn 3. Dette er den enkleste av de tre strategiene, og den du alltid prøver først.',
        },
        {
          problem: 'Hvilken er størst: 2/5 eller 2/9?',
          solution: '2/5',
          explanation: 'Samme teller: du har to biter i begge. Men et glass delt i 5 gir større biter enn et glass delt i 9. Flere deler gir mindre biter, og to store biter slår to små.',
        },
        {
          problem: 'Hvilken er størst: 2/3 eller 3/4?',
          solution: '3/4',
          explanation: 'Tolv kan deles både i tredeler og fjerdedeler: 2/3 = 8/12 og 3/4 = 9/12. Ni tolvdeler er mer enn åtte. I glasset med 12 skjeer: 8 skjeer mot 9 skjeer.',
        },
        {
          problem: 'Hvilken er størst: 3/5 eller 2/3?',
          solution: '2/3',
          explanation: 'Her lurer tallene deg, for 3 er større enn 2 og 5 er større enn 3. Gjør dem om til femtendeler: 3/5 = 9/15 og 2/3 = 10/15. Det er 2/3 som er størst. Sammenlign brøken som én størrelse, aldri teller og nevner hver for seg.',
        },
      ],
    },
    coreConceptEn: {
      definition: 'Two fractions can only be compared when they are measured against the same whole. Then there are three quick strategies: same denominator, same numerator, or comparing against the benchmarks 0, 1/2 and 1.',
      terminology: [
        { term: 'compare', definition: 'work out which of two quantities is bigger, smaller, or whether they are equal' },
        { term: 'common denominator', definition: 'a shared piece size that both fractions can be rewritten into' },
        { term: 'benchmark', definition: 'a number you measure against for a quick estimate, such as 0, 1/2 and 1' },
        { term: 'the whole', definition: 'the thing the fraction is part of — a comparison is only fair when both fractions come from the same whole' },
      ],
      explanation: 'Strategy 1 — same denominator. If the fractions share a denominator, the pieces are the same size, so all you do is count: 5/8 is bigger than 3/8, just as 5 kroner is more than 3 kroner.\n\nStrategy 2 — same numerator. If the fractions share a numerator, you have the same number of pieces, but the pieces differ. Look at 2/5 and 2/9: you have two pieces in each, but a glass split into 5 gives bigger pieces than a glass split into 9. The bigger the denominator, the smaller each piece. So 2/5 wins.\n\nStrategy 3 — benchmarks. Often it is enough to see where the fraction sits relative to 1/2 and 1. 5/12 is a little under a half, because half would be 6/12. And 5/6 is nearly whole. No calculation needed at all.\n\nWhen none of the strategies fit, you build a shared piece size. 2/3 and 3/4 have neither the same numerator nor the same denominator. But 12 can be split into thirds and into quarters: 2/3 = 8/12 and 3/4 = 9/12. Now the pieces are the same size, and 9 beats 8. So 3/4 is bigger. Both were "one piece" short — but the missing pieces were not the same size. A third is bigger than a quarter, so 2/3 is missing more.\n\nThe most dangerous habit is treating the numerator and the denominator as two ordinary numbers. Compare 3/5 and 2/3: 3 beats 2, and 5 beats 3, so "it must be bigger". Turn them into fifteenths: 3/5 = 9/15 and 2/3 = 10/15. It is 2/3 that is bigger. A fraction is one number, not two.\n\nAnd finally, the most important rule of all: a comparison only holds inside the same whole. A third of a small muffin is not more cake than a quarter of a big layer cake, even though 1/3 is bigger than 1/4. Always ask: a third of what?',
      workedExamples: [
        {
          problem: 'Which is bigger: 3/8 or 5/8?',
          solution: '5/8',
          explanation: 'The same denominator means the pieces are the same size, so all you do is count them: 5 beats 3. This is the easiest of the three strategies and the one you always try first.',
        },
        {
          problem: 'Which is bigger: 2/5 or 2/9?',
          solution: '2/5',
          explanation: 'The same numerator: two pieces in each. But a glass split into 5 gives bigger pieces than a glass split into 9. More parts means smaller pieces, and two big pieces beat two small ones.',
        },
        {
          problem: 'Which is bigger: 2/3 or 3/4?',
          solution: '3/4',
          explanation: 'Twelve can be split into thirds and into quarters: 2/3 = 8/12 and 3/4 = 9/12. Nine twelfths beats eight. In a glass that holds 12 spoonfuls: 8 spoons against 9 spoons.',
        },
        {
          problem: 'Which is bigger: 3/5 or 2/3?',
          solution: '2/3',
          explanation: 'Here the numbers trick you, because 3 beats 2 and 5 beats 3. Turn them into fifteenths: 3/5 = 9/15 and 2/3 = 10/15. It is 2/3 that is bigger. Compare the fraction as one quantity, never the numerator and the denominator separately.',
        },
      ],
    },
    interactiveComponent: {
      type: 'phet',
      description: 'Åpne den gratis simuleringen «Fractions: Intro» fra PhET (phet.colorado.edu) i nettleseren. Sett nevneren først — den bestemmer hvor store bitene blir — og fyll deretter biter til du har telleren. Bygg 2/3, se godt på figuren, og bygg så 3/4 ved siden av. Gjett før du fyller, hver eneste gang. Prøv til slutt parene 2/5 mot 2/9, og 3/5 mot 2/3.',
      phetUrl: 'https://phet.colorado.edu/en/simulations/fractions-intro',
    },
    practiceQuestions: [
      {
        id: 'fc-q1',
        type: 'multiple_choice',
        question: 'To like glass. Det ene er fylt til 2/3, det andre til 3/4. Hvilket er fullest?',
        options: ['3/4', '2/3', 'De er like fulle — begge mangler én bit', 'Umulig å si'],
        correctAnswer: '3/4',
        explanation: 'Begge mangler én bit, men bitene er ikke like store: en tredjedel er større enn en fjerdedel, så det er 2/3 som mangler mest. Regnet i tolvdeler: 2/3 = 8/12 og 3/4 = 9/12.',
        solutionSteps: [
          '🥤 Finn en bitestørrelse som passer begge: 12 kan deles i både 3 og 4',
          'Gjør om: 2/3 = 8/12 og 3/4 = 9/12',
          '9 tolvdeler er mer enn 8 tolvdeler, så 3/4 er fullest ✓',
        ],
      },
      {
        id: 'fc-q2',
        type: 'multiple_choice',
        question: 'Hvilken brøk er størst: 2/5 eller 2/9?',
        options: ['2/5', '2/9', 'De er like store', 'Den med størst nevner er alltid størst'],
        correctAnswer: '2/5',
        explanation: 'Du har to biter i begge, men et glass delt i 5 gir større biter enn et glass delt i 9. Jo større nevneren er, jo mindre er hver bit.',
        solutionSteps: [
          '🍰 Samme teller: det er to biter i begge brøkene',
          'Delt i 5 gir større biter enn delt i 9',
          'To store biter er mer enn to små, så 2/5 er størst ✓',
        ],
      },
      {
        id: 'fc-q3',
        type: 'multiple_choice',
        question: 'Hvilken brøk er størst: 3/5 eller 2/3?',
        options: ['2/3', '3/5', 'De er like store', '3/5, fordi både 3 og 5 er de største tallene'],
        correctAnswer: '2/3',
        explanation: 'Her lurer tallene deg. Gjør begge om til femtendeler: 3/5 = 9/15 og 2/3 = 10/15. Det er 2/3 som er størst. En brøk er ett tall — du kan ikke sammenligne teller og nevner hver for seg.',
        solutionSteps: [
          '⚠️ 3 er større enn 2, men det avgjør ingenting',
          '15 kan deles i både 5 og 3: 3/5 = 9/15 og 2/3 = 10/15',
          '10 femtendeler er mer enn 9, så 2/3 er størst ✓',
        ],
      },
      {
        id: 'fc-q4',
        type: 'multiple_choice',
        question: 'Emma spiser 1/3 av en liten muffins. Jonas spiser 1/4 av en stor bløtkake. Hvem har spist mest kake?',
        options: ['Det kan vi ikke vite — brøkene er tatt fra ulike helheter', 'Emma, fordi 1/3 er større enn 1/4', 'Jonas, fordi 4 er større enn 3', 'De har spist nøyaktig like mye'],
        correctAnswer: 'Det kan vi ikke vite — brøkene er tatt fra ulike helheter',
        explanation: '1/3 er større enn 1/4 bare når begge er deler av den samme helheten. En tredjedel av en liten muffins kan lett være mindre kake enn en fjerdedel av en stor bløtkake. Spør alltid: en tredjedel av hva?',
        solutionSteps: [
          '🧁 1/3 av en muffins og 1/4 av en bløtkake er deler av ulike helheter',
          'En brøk forteller hvor stor del du fikk — ikke hvor mange gram det er',
          'Uten å vite hvor store kakene er, kan vi ikke sammenligne ✓',
        ],
      },
    ],
    practiceQuestionsEn: [
      {
        id: 'fc-q1e',
        type: 'multiple_choice',
        question: 'Two identical glasses. One is filled to 2/3, the other to 3/4. Which is fuller?',
        options: ['3/4', '2/3', 'Equally full — both are one piece short', 'Impossible to say'],
        correctAnswer: '3/4',
        explanation: 'Both are one piece short, but the pieces are not the same size: a third is bigger than a quarter, so 2/3 is missing more. In twelfths: 2/3 = 8/12 and 3/4 = 9/12.',
        solutionSteps: [
          '🥤 Find a piece size that suits both: 12 splits into 3 and into 4',
          'Rewrite them: 2/3 = 8/12 and 3/4 = 9/12',
          '9 twelfths beats 8 twelfths, so 3/4 is fuller ✓',
        ],
      },
      {
        id: 'fc-q2e',
        type: 'multiple_choice',
        question: 'Which fraction is bigger: 2/5 or 2/9?',
        options: ['2/5', '2/9', 'They are equal', 'The one with the bigger denominator is always bigger'],
        correctAnswer: '2/5',
        explanation: 'You have two pieces in each, but a glass split into 5 gives bigger pieces than a glass split into 9. The bigger the denominator, the smaller each piece.',
        solutionSteps: [
          '🍰 Same numerator: there are two pieces in both fractions',
          'Split into 5 gives bigger pieces than split into 9',
          'Two big pieces beat two small ones, so 2/5 wins ✓',
        ],
      },
      {
        id: 'fc-q3e',
        type: 'multiple_choice',
        question: 'Which fraction is bigger: 3/5 or 2/3?',
        options: ['2/3', '3/5', 'They are equal', '3/5, because 3 and 5 are the bigger numbers'],
        correctAnswer: '2/3',
        explanation: 'The numbers are tricking you here. Turn both into fifteenths: 3/5 = 9/15 and 2/3 = 10/15. It is 2/3 that is bigger. A fraction is one number — you cannot compare the numerator and the denominator separately.',
        solutionSteps: [
          '⚠️ 3 is bigger than 2, but that settles nothing',
          '15 splits into both 5 and 3: 3/5 = 9/15 and 2/3 = 10/15',
          '10 fifteenths beats 9, so 2/3 is bigger ✓',
        ],
      },
      {
        id: 'fc-q4e',
        type: 'multiple_choice',
        question: 'Emma eats 1/3 of a small muffin. Jonas eats 1/4 of a big layer cake. Who has eaten more cake?',
        options: ['We cannot know — the fractions come from different wholes', 'Emma, because 1/3 is bigger than 1/4', 'Jonas, because 4 is bigger than 3', 'They have eaten exactly the same amount'],
        correctAnswer: 'We cannot know — the fractions come from different wholes',
        explanation: '1/3 is bigger than 1/4 only when both are parts of the same whole. A third of a small muffin can easily be less cake than a quarter of a big layer cake. Always ask: a third of what?',
        solutionSteps: [
          '🧁 1/3 of a muffin and 1/4 of a layer cake are parts of different wholes',
          'A fraction says what share you got — not how many grams it is',
          'Without knowing how big the cakes are, we cannot compare ✓',
        ],
      },
    ],
    handsOnActivity: {
      title: 'Tolvdelsglasset',
      materials: ['to like drikkeglass', 'en spiseskje', 'vann', 'eventuelt en dråpe konditorfarge, så nivået synes godt', 'papir og blyant'],
      instructions: [
        'Finn to like glass der det går omtrent 12 spiseskjeer i hvert — da er én skje 1/12 av glasset. Tell høyt mens dere fyller det første glasset, og noter tallet dere kom til.',
        'Barnet gjetter først: hvilket er mest, 2/3 eller 3/4 av glasset? Skriv gjettet ned på papiret før dere måler noe som helst.',
        'Regn ut sammen: 2/3 av 12 skjeer er 8 skjeer, og 3/4 av 12 skjeer er 9 skjeer.',
        'Fyll glass A med 8 skjeer og glass B med 9 skjeer. Sett dem side om side på bordet og se på nivået.',
        'Sammenlign med gjettet. Var det riktig? Og hva var det som eventuelt lurte dere?',
        'Ta en runde til med 5/6 mot 3/4 — altså 10 skjeer mot 9 skjeer. Gjett først, mål etterpå.',
      ],
      timeMinutes: 15,
      whatToLookFor: 'Se etter øyeblikket der barnet oppdager at begge brøkene måtte gjøres om til samme bitestørrelse — tolvdeler — før de i det hele tatt kunne sammenlignes. Det er hele hemmeligheten bak fellesnevner, og her tar barnet på den med hendene. Rommer glassene deres et annet antall skjeer enn 12, si det høyt og velg brøker som passer til det tallet.',
    },
    handsOnActivityEn: {
      title: 'The twelfths glass',
      materials: ['two identical drinking glasses', 'a tablespoon', 'water', 'optionally a drop of food colouring so the level shows clearly', 'paper and pencil'],
      instructions: [
        'Find two identical glasses that hold roughly 12 tablespoons each — then one spoonful is 1/12 of the glass. Count out loud while you fill the first glass, and write down the number you reach.',
        'The child guesses first: which is more, 2/3 or 3/4 of the glass? Write the guess down before you measure anything at all.',
        'Work it out together: 2/3 of 12 spoons is 8 spoons, and 3/4 of 12 spoons is 9 spoons.',
        'Fill glass A with 8 spoonfuls and glass B with 9 spoonfuls. Stand them side by side on the table and look at the levels.',
        'Compare with the guess. Was it right? And if not, what was it that fooled you?',
        'Do one more round with 5/6 against 3/4 — that is 10 spoons against 9. Guess first, measure after.',
      ],
      timeMinutes: 15,
      whatToLookFor: 'Watch for the moment the child realises that both fractions had to be turned into the same piece size — twelfths — before they could be compared at all. That is the whole secret behind common denominators, and here the child gets to touch it. If your glasses hold a different number of spoonfuls than 12, say so out loud and pick fractions that suit that number.',
    },
    printableWorksheet: {
      title: 'Gjett først, mål etterpå',
      sections: [
        {
          type: 'warmup',
          title: 'Gjett først',
          instructions: 'Sett ring rundt den du tror er størst. Ikke regn ennå — bare gjett.',
          content: '1. 3/8 eller 5/8\n2. 1/4 eller 1/6\n3. 2/3 eller 3/4\n4. 3/5 eller 2/3\n5. 7/8 eller 5/6',
        },
        {
          type: 'compare',
          title: 'Sjekk etterpå',
          instructions: 'Gjør begge brøkene om til samme bitestørrelse, og skriv < eller > mellom dem.',
          content: '1. 3/8 ___ 5/8   (samme nevner — bare tell bitene)\n2. 1/4 ___ 1/6   (samme teller — hvem har størst biter?)\n3. 2/3 ___ 3/4   (gjør om til tolvdeler)\n4. 3/5 ___ 2/3   (gjør om til femtendeler)\n5. 7/8 ___ 5/6   (gjør om til 24-deler)\n\nHvor mange av gjettene dine stemte?  ____ av 5',
        },
        {
          type: 'sequence',
          title: 'Sorter',
          instructions: 'Skriv brøkene i rekkefølge fra minst til størst.',
          content: '1/2, 3/4, 1/4, 2/3, 5/6\n\n____ , ____ , ____ , ____ , ____\n\nHint: alle fem kan skrives som tolvdeler.',
        },
        {
          type: 'challenge',
          title: 'Utfordring',
          instructions: 'Bruk holdepunktet 1/2 og svar uten å gjøre om til noe som helst.',
          content: 'Marius sier: «5/9 er mer enn halvparten, og 4/9 er mindre enn halvparten.»\n\nHar han rett? ____\n\nHvordan kan du vite det uten å regne? __________________________\n\n_____________________________________________________________',
        },
        {
          type: 'reflection',
          title: 'Refleksjon',
          instructions: 'Skriv med egne ord:',
          content: 'Hvorfor er det ikke nok å se på teller og nevner hver for seg når du skal finne ut hvilken brøk som er størst? Skriv et eksempel som viser det.\n\n_____________________________________________________________\n_____________________________________________________________',
        },
      ],
    },
    printableWorksheetEn: {
      title: 'Guess first, measure after',
      sections: [
        {
          type: 'warmup',
          title: 'Guess first',
          instructions: 'Circle the one you think is bigger. Do not calculate yet — just guess.',
          content: '1. 3/8 or 5/8\n2. 1/4 or 1/6\n3. 2/3 or 3/4\n4. 3/5 or 2/3\n5. 7/8 or 5/6',
        },
        {
          type: 'compare',
          title: 'Check afterwards',
          instructions: 'Turn both fractions into the same piece size, then write < or > between them.',
          content: '1. 3/8 ___ 5/8   (same denominator — just count the pieces)\n2. 1/4 ___ 1/6   (same numerator — whose pieces are bigger?)\n3. 2/3 ___ 3/4   (turn them into twelfths)\n4. 3/5 ___ 2/3   (turn them into fifteenths)\n5. 7/8 ___ 5/6   (turn them into twenty-fourths)\n\nHow many of your guesses were right?  ____ out of 5',
        },
        {
          type: 'sequence',
          title: 'Put them in order',
          instructions: 'Write the fractions in order from smallest to biggest.',
          content: '1/2, 3/4, 1/4, 2/3, 5/6\n\n____ , ____ , ____ , ____ , ____\n\nHint: all five can be written as twelfths.',
        },
        {
          type: 'challenge',
          title: 'Challenge',
          instructions: 'Use the benchmark 1/2 and answer without rewriting anything.',
          content: 'Marius says: "5/9 is more than half, and 4/9 is less than half."\n\nIs he right? ____\n\nHow can you tell without calculating? __________________________\n\n_____________________________________________________________',
        },
        {
          type: 'reflection',
          title: 'Reflection',
          instructions: 'Write in your own words:',
          content: 'Why is it not enough to look at the numerator and the denominator separately when working out which fraction is bigger? Write an example that shows it.\n\n_____________________________________________________________\n_____________________________________________________________',
        },
      ],
    },
    parentGuide: {
      whatChildShouldKnow: 'Barnet ditt skal nå kunne avgjøre hvilken av to brøker som er størst, og begrunne svaret — enten ved å telle biter av samme størrelse, ved å se at færre deler gir større biter, eller ved å måle mot holdepunktene 0, 1/2 og 1. Det skal også vite at en sammenligning bare gjelder innenfor den samme helheten.',
      commonMisconceptions: '«Restetenkning» er vanligst: barnet tror at 2/3 og 3/4 er like store fordi begge mangler nøyaktig én bit på å bli hele. Men bitene som mangler er ikke like store — en tredjedel er større enn en fjerdedel, så 3/4 mangler minst og er derfor størst. Den andre fellen er å sammenligne teller og nevner hver for seg, som to vanlige tall. Det gir tilfeldigvis riktig svar på 2/3 mot 3/4, men feil svar på 3/5 mot 2/3: 3 er større enn 2, men 3/5 er mindre enn 2/3. En brøk må vurderes som én størrelse — og alltid ut fra den samme helheten.',
      dinnerTableQuestion: 'Spør barnet: «Vil du ha 2/3 eller 3/4 av glasset med saft? Og hvordan vet du at du valgte mest?»',
      nextLessonPreview: 'Neste gang: Hvor på tallinja bor 3/4? Vi henger brøker på en klessnor tvers over rommet og oppdager at en brøk ikke bare er en bit av noe — den er et sted.',
    },
    parentGuideEn: {
      whatChildShouldKnow: 'Your child should now be able to decide which of two fractions is bigger and justify the answer — by counting pieces of the same size, by seeing that fewer parts means bigger pieces, or by measuring against the benchmarks 0, 1/2 and 1. They should also know that a comparison only holds inside the same whole.',
      commonMisconceptions: 'The most common error is "leftover thinking": the child believes 2/3 and 3/4 are equal because both are exactly one piece short of whole. But the missing pieces are not the same size — a third is bigger than a quarter, so 3/4 is missing least and is therefore bigger. The second trap is comparing numerator and denominator separately, as two ordinary numbers. That happens to give the right answer for 2/3 against 3/4, but the wrong answer for 3/5 against 2/3: 3 is bigger than 2, yet 3/5 is smaller than 2/3. A fraction has to be judged as one quantity — and always against the same whole.',
      dinnerTableQuestion: 'Ask your child: "Would you like 2/3 or 3/4 of the glass of juice? And how do you know you picked more?"',
      nextLessonPreview: 'Next time: Where on the number line does 3/4 live? We hang fractions on a washing line across the room and discover that a fraction is not just a piece of something — it is a place.',
    },
    openDoor: 'Nå kan du finne ut hvilken brøk som er størst. Men her er noe rart: mellom 2/3 og 3/4 — finnes det en brøk som ligger akkurat der imellom? Prøv å finne én. (Tips: gjør begge om til 24-deler først.) Og hvis du finner den, finnes det da en ny brøk mellom den og 3/4? Hvor mange ganger kan du fortsette før det ikke er plass til flere?',
    openDoorEn: 'Now you can work out which fraction is bigger. But here is something strange: between 2/3 and 3/4 — is there a fraction sitting right in the gap? Try to find one. (Hint: turn both into twenty-fourths first.) And if you find it, is there then a new fraction between that one and 3/4? How many times can you keep going before there is no room left?',
    curriculumSources: [
      'LK20 Matematikk (MAT01-05), kompetansemål etter 5. trinn: «beskrive brøk som del av ein heil, som del av ei mengd og som tal på tallinja og vurdere og namngi storleikane» (KM281). Denne leksjonen arbeider med delen «vurdere og namngi storleikane»; plassering på tallinja er tema i neste leksjon.',
      'Merk: fellesnevner som fast regnemetode er ikke nevnt i kompetansemålene etter 5. trinn. Formålstjenlige strategier for regning med brøk er et eksplisitt kompetansemål først etter 7. trinn: «utvikle og bruke formålstenlege strategiar i rekning med brøk, desimaltal og prosent og forklare tenkjemåtane sine». Her møter barnet ideen konkret, gjennom måling med skje og glass, ikke som en regel som skal pugges.',
      'LK20 Matematikk (MAT01-05), kjerneelement «Utforsking og problemløysing» — barnet gjetter først og prøver ut etterpå, slik at strategien vokser fram av erfaringen.',
    ],
    learningObjectives: [
      'Sammenligne brøker med ulike nevnere og begrunne svaret',
      'Bruke lik nevner, lik teller og holdepunktene 0, 1/2 og 1 som strategier',
      'Forklare hvorfor en sammenligning bare er rettferdig innenfor den samme helheten',
    ],
    learningObjectivesEn: [
      'Compare fractions with unlike denominators and justify the answer',
      'Use same denominator, same numerator and the benchmarks 0, 1/2 and 1 as strategies',
      'Explain why a comparison is only fair inside the same whole',
    ],
    kompetansemaal: {
      kode: 'KM281',
      text: 'beskrive brøk som del av ein heil, som del av ei mengd og som tal på tallinja og vurdere og namngi storleikane',
    },
    generatedAt: new Date(),
    version: 1,
    source: 'seed',
  },

  /* ── LEKSJON 5 — Brøk som tall på tallinja ──────────────────── */

  'fractions-on-number-line': {
    id: 'fractions-on-number-line',
    pathwayId: 'math-fractions-8-12',
    subject: 'mathematics',
    strand: 'fractions',
    ageBand: '8-12',
    phase: 'builder',
    level: 3,
    language: 'no',
    title: 'Hvor på tallinja bor 3/4?',
    titleEn: 'Where on the number line does 3/4 live?',
    durationMinutes: 40,
    saganHook: {
      type: 'question',
      content: 'Når en sang spilles av på Spotify, glir det en strek bortover skjermen. Halvveis ut i sangen står du på 1/2. Men den streken er ingen pizza — det finnes ingen biter der å telle. 1/2 er ikke en bit, det er et sted. Så hvor står 3/4? Og hvis du flytter fingeren bittelitt til høyre for midten: finnes det et tall akkurat der, eller er det tomt mellom brøkene?',
    },
    saganHookEn: "When a song plays on Spotify, a line slides across the screen. Halfway through the song you are standing on 1/2. But that line is not a pizza — there are no pieces there to count. 1/2 is not a piece, it is a place. So where is 3/4? And if you move your finger a tiny bit to the right of the middle: is there a number exactly there, or is the space between the fractions empty?",
    realWorldBridge: {
      context: 'Spotify, målebåndet og termometeret på veggen',
      content: 'Voksne bruker tallinjer hele dagen uten å kalle dem det. Avspillingsstreken i en podkast er en tallinje fra 0 til slutten av episoden. Målebåndet i verktøyskuffen er en tallinje der hver centimeter er delt i ti like deler, slik at 5 millimeter er en halv centimeter. Termometeret på veggen er en loddrett tallinje. Det alle disse har felles: tallene er ikke biter du kan telle opp, de er steder du kan peke på. Og mellom to streker finnes det alltid noe.',
    },
    realWorldBridgeEn: {
      context: 'Spotify, the tape measure and the thermometer on the wall',
      content: 'Adults use number lines all day without calling them that. The progress bar in a podcast is a number line running from 0 to the end of the episode. The tape measure in the toolbox is a number line where every centimetre is split into ten equal parts, so 5 millimetres is half a centimetre. The thermometer on the wall is a vertical number line. What they all share: the numbers are not pieces you can count up, they are places you can point at. And between any two marks there is always something.',
    },
    coreConcept: {
      definition: 'En brøk er et tall, ikke bare en bit mat. Hvert brøktall har nøyaktig ett sted på tallinja, og du finner stedet ved å måle avstand fra 0.',
      terminology: [
        { term: 'tallinje', definition: 'en rett linje der hvert tall har sitt eget faste sted' },
        { term: 'avstand fra 0', definition: 'det som avgjør hvor et tall står — ikke hvor mange streker du har telt' },
        { term: 'ekvivalente brøker', definition: 'brøker som ser ulike ut, men står på nøyaktig samme punkt på linja' },
        { term: 'uekte brøk', definition: 'en brøk med større teller enn nevner, som 5/4 — den bor til høyre for 1' },
        { term: 'blandet tall', definition: 'et helt tall og en brøk skrevet sammen, som 1 1/2 — samme sted på linja som 3/2' },
      ],
      explanation: 'Å plassere en brøk på tallinja er tre skritt. Først: finn ut hvor helheten slutter — hvor står 1? Deretter: del strekningen fra 0 til 1 i like mange deler som nevneren sier. Til slutt: gå så mange deler som telleren sier, og sett merket der. For 3/4 deler du 0 til 1 i fire like deler og går tre av dem.\n\nDet er avstanden fra 0 som bestemmer, ikke hvor mange streker du passerer. Tegn en linje fra 0 til 2 og del den i åtte like deler. Mange peker på den første streken og sier 1/8. Men helheten går bare til 1, og fra 0 til 1 er det fire deler — altså er hver del en fjerdedel. Den første streken er 1/4. Spør derfor alltid først: hvor slutter én hel?\n\nNår to brøker er ekvivalente, er de ikke naboer på linja. De er nøyaktig samme punkt. 1/2, 2/4 og 4/8 er bare tre ulike navn på ett og samme sted, omtrent som at Oslo S og Oslo sentralstasjon er samme stasjon.\n\nOg så det som pleier å overraske: brøker kan være større enn 1. 5/4 betyr fem fjerdedeler, og fire av dem fyller én hel. Den femte stikker forbi, så 5/4 står litt til høyre for 1. En slik brøk kalles en uekte brøk, og den kan også skrives som det blandede tallet 1 1/4. På linja er det samme punkt — bare to måter å lese av den samme avstanden på. På samme måte havner 6/4 og 3/2 nøyaktig oppå hverandre: midt mellom 1 og 2.',
      workedExamples: [
        {
          problem: 'Hvor står 3/4 på en linje fra 0 til 1?',
          solution: 'Tre fjerdedeler av veien fra 0 til 1',
          explanation: 'Nevneren 4 sier: del strekningen fra 0 til 1 i fire like deler. Telleren 3 sier: gå tre av dem. Merket havner mellom 1/2 og 1, nærmere 1.',
        },
        {
          problem: 'En linje fra 0 til 2 er delt i 8 like deler. Hvilket tall står ved den første streken?',
          solution: '1/4',
          explanation: 'Strekningen fra 0 til 1 er halve linja, altså fire av de åtte delene. Da er hver del 1/4, ikke 1/8. Tell alltid ut fra hvor 1 står.',
        },
        {
          problem: 'Hvor står 5/4?',
          solution: 'Litt til høyre for 1',
          explanation: 'Fem fjerdedeler: fire fjerdedeler fyller én hel, og den femte tar deg en fjerdedel forbi. 5/4 er nøyaktig samme sted som det blandede tallet 1 1/4.',
        },
        {
          problem: 'Hvor står 1/2, 2/4 og 4/8?',
          solution: 'Alle tre på nøyaktig samme punkt — midt mellom 0 og 1',
          explanation: 'De er ekvivalente brøker. På tallinja betyr ekvivalens ikke «nesten likt», men samme sted med tre ulike navn. Tre lapper må dele én klesklype.',
        },
      ],
    },
    coreConceptEn: {
      definition: 'A fraction is a number, not just a piece of food. Every fraction has exactly one place on the number line, and you find that place by measuring the distance from 0.',
      terminology: [
        { term: 'number line', definition: 'a straight line where every number has its own fixed place' },
        { term: 'distance from 0', definition: 'what decides where a number sits — not how many marks you have counted' },
        { term: 'equivalent fractions', definition: 'fractions that look different but sit on exactly the same point on the line' },
        { term: 'improper fraction', definition: 'a fraction whose numerator beats its denominator, like 5/4 — it lives to the right of 1' },
        { term: 'mixed number', definition: 'a whole number and a fraction written together, like 1 1/2 — the same place on the line as 3/2' },
      ],
      explanation: 'Placing a fraction on the number line takes three steps. First: find out where the whole ends — where is 1? Then: cut the stretch from 0 to 1 into as many parts as the denominator says. Finally: walk as many parts as the numerator says, and mark the spot. For 3/4 you cut 0 to 1 into four equal parts and walk three of them.\n\nIt is the distance from 0 that decides, not how many marks you pass. Draw a line from 0 to 2 and cut it into eight equal parts. Plenty of people point at the first mark and say 1/8. But the whole only runs to 1, and from 0 to 1 there are four parts — so each part is a quarter. The first mark is 1/4. So always ask first: where does one whole end?\n\nWhen two fractions are equivalent, they are not neighbours on the line. They are exactly the same point. 1/2, 2/4 and 4/8 are just three different names for one place, rather like Oslo S and Oslo Central Station being the same station.\n\nAnd then the part that usually surprises people: fractions can be bigger than 1. 5/4 means five quarters, and four of them fill one whole. The fifth pokes out past it, so 5/4 sits a little to the right of 1. A fraction like this is called an improper fraction, and it can also be written as the mixed number 1 1/4. On the line they are the same point — just two ways of reading the same distance. In the same way 6/4 and 3/2 land exactly on top of each other, halfway between 1 and 2.',
      workedExamples: [
        {
          problem: 'Where does 3/4 sit on a line from 0 to 1?',
          solution: 'Three quarters of the way from 0 to 1',
          explanation: 'The denominator 4 says: cut the stretch from 0 to 1 into four equal parts. The numerator 3 says: walk three of them. The mark lands between 1/2 and 1, closer to 1.',
        },
        {
          problem: 'A line from 0 to 2 is cut into 8 equal parts. What number sits at the first mark?',
          solution: '1/4',
          explanation: 'The stretch from 0 to 1 is half the line, that is four of the eight parts. So each part is 1/4, not 1/8. Always count out from where 1 sits.',
        },
        {
          problem: 'Where does 5/4 sit?',
          solution: 'A little to the right of 1',
          explanation: 'Five quarters: four quarters fill one whole, and the fifth takes you one quarter past it. 5/4 is exactly the same place as the mixed number 1 1/4.',
        },
        {
          problem: 'Where do 1/2, 2/4 and 4/8 sit?',
          solution: 'All three on exactly the same point — halfway between 0 and 1',
          explanation: 'They are equivalent fractions. On the number line, equivalent does not mean "nearly the same" — it means the same place with three different names. Three labels have to share one clothes peg.',
        },
      ],
    },
    interactiveComponent: {
      type: 'custom',
      description: 'Prøv det med linjal og blyant. Tegn en linje tvers over et ark, skriv 0 i venstre ende og 2 i høyre, og mål deg fram til hvor 1 skal stå. Sett så merker for 1/4, 3/4, 5/4 og 3/2. Finn så plassen til 1/2, så til 2/4, så til 4/8 — én om gangen. Hvor mange av dem endte på nøyaktig samme sted? Til slutt: finn et punkt som ligger mellom 3/4 og 1, og gi det et navn.',
      phetUrl: null,
    },
    practiceQuestions: [
      {
        id: 'fnl-q1',
        type: 'multiple_choice',
        question: 'En tallinje går fra 0 til 2 og er delt i 8 like deler. Hvilket tall står ved den aller første streken etter 0?',
        options: ['1/4', '1/8', '1/2', '1/16'],
        correctAnswer: '1/4',
        explanation: 'Helheten er strekningen fra 0 til 1, og den dekker fire av de åtte delene. Hver del er derfor 1/4. Det er avstanden fra 0 som teller, ikke hvor mange streker du ser på linja.',
        solutionSteps: [
          '📏 Finn 1 først: den står midt på linja',
          'Fra 0 til 1 er det 4 like deler, så hver del er 1/4',
          'Den første streken etter 0 er 1/4 ✓',
        ],
      },
      {
        id: 'fnl-q2',
        type: 'multiple_choice',
        question: 'Hvilket tall står lengst til høyre på tallinja: 3/4, 1 eller 5/4?',
        options: ['5/4', '1', '3/4', 'De står på samme sted'],
        correctAnswer: '5/4',
        explanation: '5/4 er fem fjerdedeler. Fire fjerdedeler fyller én hel, så den femte tar deg forbi 1. Brøker kan være større enn 1 — de bor bare til høyre for 1.',
        solutionSteps: [
          '🔢 3/4 mangler én fjerdedel på å nå fram til 1',
          '4/4 er nøyaktig 1, og 5/4 er én fjerdedel mer enn det',
          'Lengst til høyre står 5/4 ✓',
        ],
      },
      {
        id: 'fnl-q3',
        type: 'multiple_choice',
        question: 'Hvilke to brøker står på nøyaktig samme punkt på tallinja?',
        options: ['1/2 og 2/4', '1/2 og 2/3', '2/4 og 2/3', '1/3 og 1/2'],
        correctAnswer: '1/2 og 2/4',
        explanation: '1/2 og 2/4 er ekvivalente brøker: to av fire deler er nøyaktig halvparten. På tallinja er de ikke naboer — de er samme sted med to ulike navn.',
        solutionSteps: [
          '🎯 Del strekningen fra 0 til 1 i fire like deler',
          'To av de fire delene rekker akkurat til midten',
          'Midten er 1/2, så 1/2 og 2/4 er samme punkt ✓',
        ],
      },
      {
        id: 'fnl-q4',
        type: 'multiple_choice',
        question: 'En sang varer 4 minutter. Avspillingsstreken står på 3/4. Hvor mange minutter er spilt av?',
        options: ['3 minutter', '4 minutter', '3/4 minutt', '1 minutt'],
        correctAnswer: '3 minutter',
        explanation: 'Hele sangen er 4 minutter, så én fjerdedel er 1 minutt. Tre fjerdedeler blir 3 minutter. Brøken forteller hvor på strekningen du står — og her er strekningen tid.',
        solutionSteps: [
          '🎵 Hele sangen = 4 minutter, det er helheten',
          'Del 4 minutter i fire like deler: hver del er 1 minutt',
          '3/4 er tre slike deler, altså 3 minutter ✓',
        ],
      },
    ],
    practiceQuestionsEn: [
      {
        id: 'fnl-q1e',
        type: 'multiple_choice',
        question: 'A number line runs from 0 to 2 and is cut into 8 equal parts. What number sits at the very first mark after 0?',
        options: ['1/4', '1/8', '1/2', '1/16'],
        correctAnswer: '1/4',
        explanation: 'The whole is the stretch from 0 to 1, and it covers four of the eight parts. So each part is 1/4. What counts is the distance from 0, not how many marks you can see on the line.',
        solutionSteps: [
          '📏 Find 1 first: it sits in the middle of the line',
          'From 0 to 1 there are 4 equal parts, so each part is 1/4',
          'The first mark after 0 is 1/4 ✓',
        ],
      },
      {
        id: 'fnl-q2e',
        type: 'multiple_choice',
        question: 'Which number sits furthest to the right on the number line: 3/4, 1 or 5/4?',
        options: ['5/4', '1', '3/4', 'They are all in the same place'],
        correctAnswer: '5/4',
        explanation: '5/4 is five quarters. Four quarters fill one whole, so the fifth takes you past 1. Fractions can be bigger than 1 — they simply live to the right of 1.',
        solutionSteps: [
          '🔢 3/4 is one quarter short of reaching 1',
          '4/4 is exactly 1, and 5/4 is one quarter more than that',
          'Furthest right is 5/4 ✓',
        ],
      },
      {
        id: 'fnl-q3e',
        type: 'multiple_choice',
        question: 'Which two fractions sit on exactly the same point on the number line?',
        options: ['1/2 and 2/4', '1/2 and 2/3', '2/4 and 2/3', '1/3 and 1/2'],
        correctAnswer: '1/2 and 2/4',
        explanation: '1/2 and 2/4 are equivalent fractions: two of four parts is exactly half. On the number line they are not neighbours — they are the same place with two different names.',
        solutionSteps: [
          '🎯 Cut the stretch from 0 to 1 into four equal parts',
          'Two of those four parts reach exactly to the middle',
          'The middle is 1/2, so 1/2 and 2/4 are the same point ✓',
        ],
      },
      {
        id: 'fnl-q4e',
        type: 'multiple_choice',
        question: 'A song lasts 4 minutes. The progress bar is at 3/4. How many minutes have played?',
        options: ['3 minutes', '4 minutes', '3/4 of a minute', '1 minute'],
        correctAnswer: '3 minutes',
        explanation: 'The whole song is 4 minutes, so one quarter is 1 minute. Three quarters is 3 minutes. The fraction tells you where along the stretch you are standing — and here the stretch is time.',
        solutionSteps: [
          '🎵 The whole song = 4 minutes, that is the whole',
          'Cut 4 minutes into four equal parts: each part is 1 minute',
          '3/4 is three such parts, so 3 minutes ✓',
        ],
      },
    ],
    handsOnActivity: {
      title: 'Klessnor-tallinja',
      materials: ['hyssing eller et snøre på minst to meter', 'klesklyper', 'papirlapper', 'tusj', 'målebånd eller tommestokk'],
      instructions: [
        'Spenn snøret tvers over rommet. Heng en lapp med 0 i den ene enden og en lapp med 2 i den andre.',
        'Mål med målebåndet og heng lappen med 1 nøyaktig på midten — ikke på øyemål. Snakk om hvorfor det må måles: står 1 feil, står alt annet feil.',
        'Skriv disse brøkene på hver sin lapp: 1/4, 1/2, 2/4, 4/8, 3/4, 7/8, 5/4, 6/4 og 3/2.',
        'Barnet henger opp én og én lapp med klesklype, og sier høyt hvordan det tenkte før klypen klikkes fast.',
        'Gå langs snøret sammen når alle lappene henger: havnet 1/2, 2/4 og 4/8 på nøyaktig samme sted? Og 6/4 og 3/2?',
        'Avslutt med utfordringen: lag en ny lapp med en brøk som skal henge mellom 3/4 og 1. Klarer dere å finne to?',
      ],
      timeMinutes: 15,
      whatToLookFor: 'Tør barnet å henge 5/4 til høyre for 1? Og forstår det at ekvivalente brøker ikke er naboer på linja, men nøyaktig samme punkt — slik at tre lapper må dele én klesklype? Hvis barnet begynner å telle klyper i stedet for å måle avstand fra 0, gå tilbake til lappen med 1 og spør: hvor slutter én hel?',
    },
    handsOnActivityEn: {
      title: 'The washing-line number line',
      materials: ['string at least two metres long', 'clothes pegs', 'paper labels', 'a marker pen', 'a tape measure or folding ruler'],
      instructions: [
        'Stretch the string across the room. Hang a label reading 0 at one end and a label reading 2 at the other.',
        'Measure with the tape and hang the label reading 1 exactly at the midpoint — not by eye. Talk about why it has to be measured: if 1 is in the wrong place, everything else is too.',
        'Write these fractions on separate labels: 1/4, 1/2, 2/4, 4/8, 3/4, 7/8, 5/4, 6/4 and 3/2.',
        'The child pegs the labels up one at a time, saying out loud how they worked it out before the peg clicks shut.',
        'Walk along the string together once everything is hanging: did 1/2, 2/4 and 4/8 land on exactly the same spot? And 6/4 and 3/2?',
        'Finish with the challenge: make a new label with a fraction that belongs between 3/4 and 1. Can you find two?',
      ],
      timeMinutes: 15,
      whatToLookFor: 'Does the child dare to hang 5/4 to the right of 1? And do they grasp that equivalent fractions are not neighbours on the line but exactly the same point — so three labels have to share one peg? If the child starts counting pegs instead of measuring distance from 0, go back to the label reading 1 and ask: where does one whole end?',
    },
    printableWorksheet: {
      title: 'Tallinja: brøker som steder',
      sections: [
        {
          type: 'warmup',
          title: 'Oppvarming',
          instructions: 'Sett et kryss på tallinja fra 0 til 1. Del opp strekningen først.',
          content: '0 |—————————————————————————| 1\n\n1. 1/2\n2. 1/4\n3. 3/4\n4. 7/8',
        },
        {
          type: 'label',
          title: 'Les av linja',
          instructions: 'Linja går fra 0 til 2 og er delt i 8 like deler. Skriv brøken som hører til hver strek. Finn ut hvor 1 står før du skriver noe.',
          content: 'Strek 1: ____   Strek 2: ____   Strek 3: ____   Strek 4: ____\n\nStrek 5: ____   Strek 6: ____   Strek 7: ____',
        },
        {
          type: 'match',
          title: 'Samme sted',
          instructions: 'Trekk strek mellom brøkene som står på nøyaktig samme punkt på tallinja.',
          content: '1/2          6/8\n2/3          3/2\n3/4          4/8\n6/4          8/12',
        },
        {
          type: 'challenge',
          title: 'Utfordring',
          instructions: 'Finn brøken som mangler.',
          content: 'a) Skriv en brøk som ligger mellom 1/2 og 3/4.  Svar: ______\n   Hint: gjør begge om til åttedeler først.\n\nb) Finn så en brøk som ligger mellom svaret ditt og 3/4.  Svar: ______\n\nc) Hvor lenge tror du man kan holde på slik? ____________________',
        },
        {
          type: 'reflection',
          title: 'Refleksjon',
          instructions: 'Skriv med egne ord:',
          content: 'Hva mener vi når vi sier at 1/2 ikke er en bit, men et sted?\n\n_____________________________________________________________\n_____________________________________________________________',
        },
      ],
    },
    printableWorksheetEn: {
      title: 'The number line: fractions as places',
      sections: [
        {
          type: 'warmup',
          title: 'Warm-up',
          instructions: 'Mark a cross on the number line from 0 to 1. Split the stretch up first.',
          content: '0 |—————————————————————————| 1\n\n1. 1/2\n2. 1/4\n3. 3/4\n4. 7/8',
        },
        {
          type: 'label',
          title: 'Read the line',
          instructions: 'The line runs from 0 to 2 and is cut into 8 equal parts. Write the fraction belonging to each mark. Work out where 1 sits before you write anything.',
          content: 'Mark 1: ____   Mark 2: ____   Mark 3: ____   Mark 4: ____\n\nMark 5: ____   Mark 6: ____   Mark 7: ____',
        },
        {
          type: 'match',
          title: 'Same place',
          instructions: 'Draw a line between the fractions that sit on exactly the same point.',
          content: '1/2          6/8\n2/3          3/2\n3/4          4/8\n6/4          8/12',
        },
        {
          type: 'challenge',
          title: 'Challenge',
          instructions: 'Find the missing fraction.',
          content: 'a) Write a fraction that lies between 1/2 and 3/4.  Answer: ______\n   Hint: turn both into eighths first.\n\nb) Now find a fraction that lies between your answer and 3/4.  Answer: ______\n\nc) How long do you think you could keep this up? ________________',
        },
        {
          type: 'reflection',
          title: 'Reflection',
          instructions: 'Write in your own words:',
          content: 'What do we mean when we say that 1/2 is not a piece, but a place?\n\n_____________________________________________________________\n_____________________________________________________________',
        },
      ],
    },
    parentGuide: {
      whatChildShouldKnow: 'Barnet ditt skal nå kunne plassere en brøk på en tallinje ved å måle avstand fra 0, se at ekvivalente brøker havner på nøyaktig samme punkt, og godta at brøker større enn 1 — som 5/4 — hører hjemme til høyre for 1.',
      commonMisconceptions: 'Mange barn leser 3/4 som to tall med en strek imellom, ikke som ett tall. Da blir spørsmålet «hvor står brøken?» nesten umulig å svare på, og det blir enda vanskeligere å godta at en brøk kan være større enn 1 — for hvordan kan man ha flere biter enn hele? På tallinja ser barnet at 5/4 rett og slett er stedet litt til høyre for 1. Den andre fellen er å telle streker i stedet for å måle avstand fra 0: på en linje fra 0 til 2 som er delt i åtte like deler, er den første streken 1/4 — ikke 1/8. Spør «hvor slutter én hel?» før dere teller noe som helst.',
      dinnerTableQuestion: 'Spør barnet: «Sangen varer 4 minutter, og streken står på 3/4. Hvor lenge er det igjen — og hvordan så du det?»',
      nextLessonPreview: 'Dette var siste leksjon i brøkløypa. Neste steg, som barnet møter på 6. og 7. trinn på skolen, er desimaltall og prosent: 0,75 og 75 % er nøyaktig samme sted på tallinja som 3/4. Samme tall, nye klær.',
    },
    parentGuideEn: {
      whatChildShouldKnow: 'Your child should now be able to place a fraction on a number line by measuring the distance from 0, see that equivalent fractions land on exactly the same point, and accept that fractions bigger than 1 — such as 5/4 — belong to the right of 1.',
      commonMisconceptions: 'Many children read 3/4 as two numbers with a line between them, not as one number. That makes the question "where does the fraction sit?" nearly impossible to answer, and it makes it even harder to accept that a fraction can be bigger than 1 — how can you have more pieces than the whole? On the number line the child sees that 5/4 is simply the place a little to the right of 1. The second trap is counting marks instead of measuring distance from 0: on a line from 0 to 2 cut into eight equal parts, the first mark is 1/4 — not 1/8. Ask "where does one whole end?" before you count anything at all.',
      dinnerTableQuestion: 'Ask your child: "The song lasts 4 minutes and the progress bar is at 3/4. How much is left — and how did you see it?"',
      nextLessonPreview: 'That was the last lesson in the fractions pathway. The next step, which your child meets in school in years 6 and 7, is decimals and percentages: 0.75 and 75% sit on exactly the same spot on the number line as 3/4. Same number, new clothes.',
    },
    openDoor: 'Mellom 1/2 og 3/4 fant du 5/8. Mellom 1/2 og 5/8 finnes 9/16. Og mellom dem igjen: 17/32. Du kan holde på så lenge du orker, og det dukker alltid opp en ny brøk i mellomrommet. Så finnes det i det hele tatt en minste brøk som er større enn null? Og hvis det ikke gjør det — hvordan klarer streken på skjermen likevel å komme seg helt fram til slutten av sangen?',
    openDoorEn: 'Between 1/2 and 3/4 you found 5/8. Between 1/2 and 5/8 sits 9/16. And between those two: 17/32. You can keep going for as long as you have patience, and a new fraction always turns up in the gap. So is there such a thing as a smallest fraction bigger than zero? And if there is not — how does the line on the screen still manage to reach the end of the song?',
    curriculumSources: [
      'LK20 Matematikk (MAT01-05), kompetansemål etter 5. trinn: «beskrive brøk som del av ein heil, som del av ei mengd og som tal på tallinja og vurdere og namngi storleikane» (KM281). Denne leksjonen arbeider med delen «som tal på tallinja»; storleiksvurdering er dekket i forrige leksjon. Leksjonen bygger videre på kompetansemålet etter 2. trinn: «plassere tal på tallinja og bruke tallinja i rekning og problemløysing».',
      'Merk: uttrykkene «uekte brøk» og «blandet tall» er ikke nevnt i noe kompetansemål i LK20 — heller ikke etter 7. trinn. De er tatt med her fordi brøker større enn 1 dukker opp av seg selv med en gang man plasserer brøk som tall på tallinja. Barnet skal kjenne igjen fenomenet, ikke prøves i begrepene.',
      'Merk: at det alltid finnes en ny brøk mellom to brøker, er ikke et kompetansemål på noe trinn. Det står her som et åpent undringsspørsmål, ikke som noe barnet skal kunne. Påstanden er matematisk riktig — mellom to brøktall ligger det alltid uendelig mange andre.',
      'LK20 Matematikk (MAT01-05), kjerneelement «Representasjon og kommunikasjon» — den samme brøken vises som bit, som punkt på en linje og som avspillingsstrek.',
    ],
    learningObjectives: [
      'Plassere brøker på en tallinje ved å måle avstand fra 0',
      'Se at ekvivalente brøker er nøyaktig samme punkt, ikke naboer',
      'Plassere brøker som er større enn 1 til høyre for 1',
    ],
    learningObjectivesEn: [
      'Place fractions on a number line by measuring the distance from 0',
      'See that equivalent fractions are exactly the same point, not neighbours',
      'Place fractions bigger than 1 to the right of 1',
    ],
    kompetansemaal: {
      kode: 'KM281',
      text: 'beskrive brøk som del av ein heil, som del av ei mengd og som tal på tallinja og vurdere og namngi storleikane',
    },
    generatedAt: new Date(),
    version: 1,
    source: 'seed',
  },
};
